/**
 * Client 360's **People** tab — who acts for this client (FR-B2), their
 * invitations (FR-B3) and the approval switch (C8 §3b). Split out of
 * `index.tsx` in Wave 15B (C14 §10.6 item 3; T-6.14).
 *
 * Every act here is `person:can_update`, as it was: *Link someone*, *Invite*
 * and *Remove*. Since the redraw **Invite and Remove ask first** — §10.6
 * item 1 and ADR-0033 §1: anything that sends or deletes asks through
 * `ConfirmDialog`. What each one calls is unchanged, and a refusal is now said
 * in the dialog instead of being dropped.
 */

import { Badge, Button, ConfirmDialog, formatDate } from '@fiveox/ui'
import { Mail, Send, UserPlus, Users, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  type ListColumn,
  RecordSection,
  ListTable,
  humanise,
  useIsPhone,
  useListTable,
} from '../../components/page'
import { PERSON, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import type { ClientRecord, InvitationRow, LinkedPerson } from '../../lib/api/clients'
import {
  type LinkRole,
  LINK_ROLES,
  ROLE_LABELS,
  canUnlink,
  inviteFailureCopy,
  inviteOutcome,
  persons,
} from '../../lib/api/persons'
import { ApprovalSwitch } from './ApprovalSwitch'
import { LinkPersonDialog } from './LinkPersonDialog'

type Asking = { kind: 'invite' | 'remove'; person: LinkedPerson }

export function PeopleTab({
  client,
  people,
  invitations,
  onChanged,
  onNotice,
}: {
  client: ClientRecord
  people: LinkedPerson[]
  invitations: InvitationRow[]
  onChanged: () => void
  onNotice: (notice: { text: string; tone: 'success' | 'info' }) => void
}) {
  const { can } = useEvaluation()
  const mayManage = can(PERSON, 'can_update')
  // On a phone the two row actions are icons with their names for a screen
  // reader: with words they cover the role column.
  const phone = useIsPhone()

  const [linking, setLinking] = useState(false)
  const [asking, setAsking] = useState<Asking | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    setAsking(null)
    setError(null)
  }

  const invite = async (person: LinkedPerson) => {
    // Re-inviting keeps the role they already hold — the service treats a
    // second invitation to the same entity as a re-send, not a second link, so
    // sending a different role here would be a silent demotion.
    const result = await persons.invite(person.person_id, {
      client_id: client.id,
      role: (LINK_ROLES.includes(person.role as LinkRole) ? person.role : 'authorised') as LinkRole,
    })
    // **`sent: false` is an ordinary outcome, not an error.** The invitation was
    // made — it is a record, a link and an audit line — and core-communication
    // fails closed by design. Saying "invitation failed" here when the person
    // is visibly on the entity is the confusing version. A message sent
    // *without* its sign-in link (C27 item 5, UAT report #10) is said too.
    onNotice(inviteOutcome(result.data))
  }

  const confirm = async () => {
    if (!asking) return
    setBusy(true)
    setError(null)
    try {
      if (asking.kind === 'invite') await invite(asking.person)
      else await persons.unlink(asking.person.person_id, client.id)
      close()
      onChanged()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be done. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo<ListColumn<LinkedPerson>[]>(
    () => [
      {
        key: 'full_name',
        label: 'Name',
        width: 200,
        required: true,
        phone: true,
        sortField: 'full_name',
        render: (person) => (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-foreground font-medium">{person.full_name}</span>
            {person.is_self && <Badge size="sm">this client</Badge>}
          </span>
        ),
      },
      {
        key: 'role',
        label: 'Role',
        width: 110,
        phone: true,
        sortField: 'role',
        render: (person) => (
          <span className="text-muted-foreground">{ROLE_LABELS[person.role] ?? person.role}</span>
        ),
      },
      {
        key: 'contact',
        label: 'Contact',
        width: 200,
        render: (person) => (
          <span className="text-muted-foreground">{person.email ?? person.mobile ?? '—'}</span>
        ),
      },
      {
        key: 'linked_at',
        label: 'Linked',
        width: 110,
        sortField: 'linked_at',
        render: (person) => (
          <span className="text-muted-foreground">
            {person.linked_at ? formatDate(person.linked_at) : '—'}
          </span>
        ),
      },
    ],
    [],
  )
  const list = useListTable(people, { columns })

  const subject = asking?.person.full_name ?? ''

  return (
    <div className="min-w-0 space-y-6">
      <RecordSection
        id="client-people"
        icon={<Users size={18} aria-hidden />}
        title="People"
        description="Who acts for this client. A person is not a client — they act for one."
        actions={
          mayManage ? (
            <Button variant="outline" size="sm" onClick={() => setLinking(true)}>
              <UserPlus size={14} aria-hidden /> Link someone
            </Button>
          ) : undefined
        }
      >
        <div className="space-y-4">
          <ListTable
            list={list}
            loading={false}
            rowKey={(person) => person.person_id}
            empty="Nobody manages this client’s account yet."
            trailing={
              mayManage
                ? {
                    header: '',
                    width: phone ? 88 : 190,
                    cell: (person) => (
                      <span className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={phone ? `Invite ${person.full_name}` : undefined}
                          onClick={() => setAsking({ kind: 'invite', person })}
                        >
                          <Mail size={14} aria-hidden /> {!phone && 'Invite'}
                        </Button>
                        {canUnlink(person, people) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={phone ? `Remove ${person.full_name}` : undefined}
                            onClick={() => setAsking({ kind: 'remove', person })}
                          >
                            <X size={14} aria-hidden /> {!phone && 'Remove'}
                          </Button>
                        )}
                      </span>
                    ),
                  }
                : undefined
            }
          />
          {/* w9f — T-5.32, the staff half of C8 §3b. Under the list, because it
              is a statement about the roles that list has just shown; see
              `./ApprovalSwitch.tsx` for why it is not in the Edit dialog. */}
          <ApprovalSwitch client={client} people={people} onChanged={onChanged} />
        </div>
      </RecordSection>

      {invitations.length > 0 && (
        <RecordSection
          id="client-invitations"
          icon={<Send size={18} aria-hidden />}
          title="Invitations"
          description="Invitations to your client portal, and whether they reached anyone."
        >
          <ul className="divide-border divide-y text-sm">
            {invitations.map((invitation) => (
              <li key={invitation.id} className="flex flex-wrap items-center gap-2 py-2.5 first:pt-0 last:pb-0">
                <span className="text-foreground font-medium">
                  {ROLE_LABELS[invitation.role] ?? invitation.role}
                </span>
                <Badge size="sm">{humanise(invitation.status)}</Badge>
                {invitation.channel && (
                  <span className="text-muted-foreground">via {invitation.channel}</span>
                )}
                <span className="text-muted-foreground ml-auto text-xs">{formatDate(invitation.at)}</span>
                {invitation.failure_reason && (
                  <p className="text-muted-foreground w-full text-xs">
                    {inviteFailureCopy(invitation.failure_reason)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </RecordSection>
      )}

      <ConfirmDialog
        open={asking?.kind === 'invite'}
        onClose={close}
        title={`Invite ${subject}?`}
        confirmLabel="Send invitation"
        cancelLabel="Don’t send"
        busy={busy}
        busyLabel="Sending…"
        error={error}
        onConfirm={() => void confirm()}
      >
        We’ll send {subject} an invitation to your client portal, as{' '}
        {ROLE_LABELS[asking?.person.role ?? ''] ?? 'authorised'} on {client.legal_name}. Their role
        does not change.
      </ConfirmDialog>

      <ConfirmDialog
        open={asking?.kind === 'remove'}
        onClose={close}
        title={`Remove ${subject}?`}
        confirmLabel="Remove"
        cancelLabel="Keep them"
        tone="danger"
        busy={busy}
        busyLabel="Removing…"
        error={error}
        onConfirm={() => void confirm()}
      >
        {subject} will no longer act for {client.legal_name}. Their own record stays, and you can
        link them again later.
      </ConfirmDialog>

      {linking && (
        <LinkPersonDialog
          client={client}
          existing={people}
          onClose={() => setLinking(false)}
          onLinked={(undelivered) => {
            setLinking(false)
            if (undelivered) onNotice({ text: undelivered, tone: 'info' })
            onChanged()
          }}
        />
      )}
    </div>
  )
}
